import type { Item, ItemQuery } from "@iltocat/shared";
import { api } from "./api.js";
import { pooled } from "./pool.js";

const CONCURRENCY = 6;

/**
 * Every whole item matching `query` (all of them, when there is none).
 *
 * `GET /api/items` answers with summaries; the whole item -- which is what
 * carries private fields and notes -- takes a second round trip per id. At the
 * ~100-item scale this app is sized for that is a few hundred requests at
 * most, fetched a handful at a time (`pooled()`) rather than all at once.
 * Shared by the Stage 5 zip export and the CSV export.
 */
export async function allItems(
  onProgress: (loaded: number, total: number) => void,
  query: ItemQuery = {},
): Promise<Item[]> {
  const summaries: { id: string }[] = [];
  let cursor: string | null = null;
  let total = 0;
  do {
    const page = await api.items.list({ ...query, limit: 100, ...(cursor ? { cursor } : {}) });
    summaries.push(...page.items);
    total = page.total;
    cursor = page.nextCursor;
  } while (cursor);

  let loaded = 0;
  return pooled(summaries, CONCURRENCY, async ({ id }) => {
    const { item } = await api.items.get(id);
    loaded += 1;
    onProgress(loaded, total);
    return item;
  });
}
